import { Typography } from "@mui/material";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useExports } from "api/exports";
import {
  hasModifyAccessToResource,
  hasViewAccessToResource,
} from "api/general";
import { useMember } from "api/members";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderActionButton } from "components/Header/Subheader/SubheaderActionButton";
import { SubheaderLinkButton } from "components/Header/Subheader/SubheaderLinkButton";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { ReactComponent as ArrowCircleBrokenUpIcon } from "components/Icons/arrow-circle-broken-up.svg";
import { ReactComponent as Settings01Icon } from "components/Icons/settings-01.svg";
import { parseFiltersForExport } from "components/Layout/Export/helpers";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { TierTooltip } from "components/Tooltips/Templates/Tier";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { MEMBER_LIST_ID } from "state/tables/types";
import { ExportType } from "types/exports";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { BackendMemberModel } from "types/members";
import { backendErrors } from "utilities/backendErrors";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import {
  abbreviateNumber,
  formatCurrencyValue,
  formatNumberValue,
} from "utilities/numbers";
import { useStyles } from "./MemberList.styles";
import { MemberModals } from "./MemberModals";

const createItems = (
  items: BackendMemberModel[],
  classes: { [key: string]: string },
): Array<Row> => {
  return items.map((item) => ({
    cells: [
      {
        content: `${
          item.firstName || item.lastName
            ? `${item.firstName} ${item.lastName}`
            : "-"
        }`,
        property: "firstName",
        dataHjSuppress: true,
      },
      { content: item.email, property: "email", dataHjSuppress: true },
      { content: OLDate(item.createdAt), property: "createdAt" },
      { content: OLDate(item.registeredAt), property: "registeredAt" },
      {
        align: "right",
        content: item.customerId,
        property: "memberId",
        dataHjSuppress: true,
      },
      {
        align: "right",
        content: formatNumberValue(item.transactionsCount),
        emptyString: "0",
        property: "transactionsCount",
      },
      {
        align: "right",
        content: formatNumberValue(item.defaultAccount?.earnedPoints),
        emptyString: "0",
        property: "defaultAccount:earnedPoints",
      },
      {
        align: "right",
        content: formatNumberValue(item.defaultAccount?.activePoints),
        emptyString: "0",
        property: "defaultAccount:activePoints",
      },
      {
        align: "right",
        content: formatCurrencyValue(item.transactionsAmount),
        emptyString: "0",
        property: "transactionsAmount",
      },
      {
        align: "right",
        content: formatCurrencyValue(item.averageTransactionAmount),
        emptyString: "0",
        property: "averageTransactionAmount",
      },
      {
        content: item.currentLevel?.name ? (
          <Tooltip
            text={
              <TierTooltip
                title={item.currentLevel?.name || ""}
                isManuallyAssigned={!!item.manuallyAssignedLevelId}
              />
            }
          >
            <div className={classes.chips}>
              {item.manuallyAssignedLevelId && (
                <ArrowCircleBrokenUpIcon data-test-id="manually-assign-icon" />
              )}
              <Typography className={classes.tooltipTitle}>
                {item.currentLevel?.name}
              </Typography>
            </div>
          </Tooltip>
        ) : (
          "-"
        ),
        property: "tier",
        maxWidth: "300px",
      },
      {
        align: "right",
        content: item.loyaltyCardNumber,
        property: "loyaltyCardNumber",
        dataHjSuppress: true,
      },
    ],
    id: item.customerId,
    metadata: {
      anonymized: item.anonymized,
      disabled: !item.active,
      manuallyAssignedLevel: !!item.manuallyAssignedLevelId,
    },
    testId: "members-table-row",
  }));
};

export enum Modals {
  Activate,
  Add,
  Anonymize,
  AssignToTier,
  ChangePassword,
  Deactivate,
  Deduct,
  ImportMembers,
  MemberDetailsDrawer,
  Remove,
  RemoveManuallyAssignedTier,
}

export function MemberList() {
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing(MEMBER_LIST_ID);
  const appDispatch = useAppDispatch();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const tables = useAppSelector(selectTables);
  const [filters, setFilters] = useState<SelectedFilter[]>(
    () =>
      tables[MEMBER_LIST_ID]?.filters?.filter(
        (item: SelectedFilter) => item.type !== "dynamic-search",
      ) || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      MEMBER_LIST_ID,
    );

    setFilters(filters);
  };
  const hasModifyExportPermission = hasModifyAccessToResource("EXPORT");
  const hasViewImportPermission = hasViewAccessToResource("IMPORT");

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [selectedModal, setModal] = useState<{
    member: string;
    mode: number;
  }>();
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<any>();
  const classes = useStyles();

  const { useMembersList } = useMember();
  const { data, error } = useMembersList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "createdAt",
    dynamicSearch,
  });
  const { exportList } = useExports();

  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultWallet = useAppSelector(selectDefaultWalletData);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "fullName" }),
      property: "firstName",
      hasMinWidthThreshold: true,
      testId: "members-table-header-full-name",
      isSortable: true,
    },
    {
      headerText: message({ id: "email" }),
      property: "email",
      hasMinWidthThreshold: true,
      testId: "members-table-header-email",
      isSortable: true,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      hasMinWidthThreshold: true,
      testId: "members-table-header-created-at-date",
      isSortable: true,
    },
    {
      headerText: message({ id: "registrationDate" }),
      property: "registeredAt",
      testId: "members-table-header-registration-date",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "memberID" }),
      testId: "members-table-header-member-id",
      property: "memberId",
      isVisible: false,
    },
    {
      align: "right",
      headerText: message({ id: "transactions" }),
      property: "transactionsCount",
      testId: "members-table-header-transactions",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message(
        { id: "totalEarnedUnits" },
        { units: defaultWallet?.unitPluralName },
      ),
      property: "defaultAccount:earnedPoints",
      testId: "members-table-header-total-earned-units",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message(
        { id: "activeUnitSingularOrPlural" },
        { units: defaultWallet?.unitPluralName },
      ),
      property: "defaultAccount:activePoints",
      testId: "members-table-header-active-units",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "totalSpendingWithCurrency" }, { currency }),
      property: "transactionsAmount",
      testId: "members-table-header-total-spending",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message(
        { id: "avgTransactionValueWithCurrency" },
        { currency },
      ),
      property: "averageTransactionAmount",
      testId: "members-table-header-avg-transaction-value",
      isSortable: true,
    },
    {
      headerText: message({ id: "tier" }),
      testId: "members-table-header-tier",
      property: "tier",
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      testId: "members-table-header-loyaltyCardNumber",
      isVisible: false,
      property: "loyaltyCardNumber",
    },
  ]);

  const items = data?.items ? createItems(data.items, classes) : [];

  const handleExportList = async () => {
    const filtersObject = dynamicSearch?.value
      ? parseFiltersForExport([
          ...filters,
          {
            // Add dynamicSearch condition at the end to override same condition in filters, "like" condition only
            ...dynamicSearch,
            condition: "like",
            type: "dynamic-search-like",
          },
        ])
      : parseFiltersForExport(filters);

    try {
      const data = await exportList(ExportType.MEMBER, {
        member: {
          _orderBy: { createdAt: "desc" },
          _page: listing.page,
          _itemsOnPage: listing.rowsPerPage,
          ...filtersObject,
        },
      });

      if (data) {
        notify(message({ id: "exportSnackbarInfo" }), {
          button: {
            text: message({ id: "viewList" }),
            onClick: ({ closeNotification }) => {
              closeNotification();

              return navigate(links.importsExports, {
                state: { activeTab: "exports" },
              });
            },
          },
        });
        setErrors(null);
        setConfirmationModalOpen(false);
      }
    } catch (exception) {
      notify(getErrorMessage(exception));
      setErrors(backendErrors(getBackendErrors(exception)));
    }
  };

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, MEMBER_LIST_ID);
    }
    setPersistentFilters(filters, MEMBER_LIST_ID);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  return (
    <>
      <TopHeader title={message({ id: "members" })} />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "members" })}
          setFilters={setSelectedFilters}
          total={data?.total}
          data-test-id="member-list"
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "active",
              label: message({ id: "accountStatus" }),
              type: "boolean-status",
            },
            {
              query: "defaultAccount:activePoints",
              label: message(
                { id: "activeUnitSingularOrPlural" },
                { units: defaultWallet?.unitPluralName },
              ),
              type: "number",
            },
            {
              query: "address:address1",
              label: message({ id: "buildingName" }),
              type: "text",
            },
            {
              query: "address:address2",
              label: message({ id: "flatUnitName" }),
              type: "text",
            },
            {
              query: "agreement1",
              label: message({ id: "agreement1" }),
              type: "boolean",
            },
            {
              query: "agreement2",
              label: message({ id: "agreement2" }),
              type: "boolean",
            },
            {
              query: "agreement3",
              label: message({ id: "agreement3" }),
              type: "boolean",
            },
            {
              query: "amountExcludedForLevel",
              label: message({ id: "amountExcludedForTier" }),
              type: "number",
            },
            {
              query: "averageTransactionAmount",
              label: message(
                { id: "avgTransactionValueWithCurrency" },
                { currency },
              ),
              type: "number",
            },
            {
              query: "address:city",
              label: message({ id: "city" }),
              type: "text",
            },
            {
              query: "address:country",
              label: message({ id: "country" }),
              type: "country-filter",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "registeredAt",
              label: message({ id: "registrationDate" }),
              type: "datetime",
            },
            {
              query: "birthDate",
              label: message({ id: "dateOfBirth" }),
              type: "datetime",
            },
            {
              query: "email",
              label: message({ id: "email" }),
              type: "member-email-filter",
              dataHjSuppress: true,
            },
            {
              query: "firstName",
              label: message({ id: "firstName" }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "firstTransactionDate",
              label: message({ id: "firstTransactionDate" }),
              type: "datetime",
            },
            {
              query: "gender",
              label: message({ id: "gender" }),
              type: "gender",
              dataHjSuppress: true,
            },
            {
              query: "referralToken",
              label: message({ id: "referralToken" }),
              type: "text",
            },
            {
              query: "lastName",
              label: message({ id: "lastName" }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "lastTransactionDate",
              label: message({ id: "lastTransactionDate" }),
              type: "datetime",
            },
            {
              query: "loyaltyCardNumber",
              label: message({ id: "loyaltyCardNumber" }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "manuallyAssignedLevelId",
              label: message({ id: "manuallyAssignedTier" }),
              type: "tier-name-with-id-filter",
            },
            {
              query: "customerId",
              label: message({ id: "memberID" }),
              type: "text",
              dataHjSuppress: true,
            },
            {
              query: "phone",
              label: message({ id: "phoneNumber" }),
              type: "member-phone-filter",
              dataHjSuppress: true,
            },
            {
              query: "address:postal",
              label: message({ id: "postalCode" }),
              type: "text",
            },
            {
              query: "address:province",
              label: message({ id: "province" }),
              type: "text",
            },
            {
              query: "address:street",
              label: message({ id: "street" }),
              type: "text",
            },
            {
              query: "levelAchievementDate",
              label: message({ id: "tierReachedDate" }),
              type: "datetime",
            },
            {
              query: "levelId",
              label: message({ id: "tierName" }),
              type: "tier-name-with-id-filter",
            },
            {
              query: "defaultAccount:earnedPoints",
              label: message(
                { id: "totalEarnedUnits" },
                { units: defaultWallet?.unitPluralName },
              ),
              type: "number",
            },
            {
              query: "transactionsAmount",
              label: message({ id: "transactionsAmount" }),
              type: "number",
            },
            {
              query: "transactionsAmountWithoutDeliveryCosts",
              label: message({
                id: "transactionsAmountWithoutDeliveryCosts",
              }),
              type: "number",
            },
            {
              query: "transactionsCount",
              label: message({ id: "transactionsCount" }),
              type: "number",
            },
            {
              query: "updatedAt",
              label: message({ id: "updatedAt" }),
              type: "datetime",
            },
          ]}
        />
        <SubheaderWrapper>
          {hasModifyExportPermission && (
            <SubheaderActionButton
              data-test-id="export-to-csv"
              disabled={data?.total.filtered === 0}
              disabledInfo={{
                name: message({ id: "exportIsUnavailable" }),
                text: message({ id: "exportIsUnavailableInfo" }),
              }}
              onClick={() => setConfirmationModalOpen(true)}
              text={message(
                { id: "exportToCSVFile" },
                { value: abbreviateNumber(data?.total.filtered) },
              ).toUpperCase()}
            />
          )}
          <SubheaderLinkButton
            data-test-id="configuration"
            icon={<Settings01Icon />}
            link={links.membersConfiguration}
            text={message({ id: "configuration" })}
            variant="outlined"
          />
          <SubheaderMenuButton
            text={message({ id: "addMember" })}
            menuItems={[
              {
                text: message({ id: "addSingleMember" }),
                onClick: () => navigate(links.addMember),
              },
              {
                text: message({ id: "importMembers" }),
                onClick: () => {
                  setModal({ member: "", mode: Modals.ImportMembers });
                  setOpen(true);
                },
                hideCondition: hasViewImportPermission,
              },
            ]}
            data-test-id="member-list"
          />
        </SubheaderWrapper>
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="email"
            properties={[
              {
                id: "email",
                label: "email",
              },
              {
                id: "lastName",
                label: "lastName",
              },
              {
                id: "phone",
                label: "phone",
              },
              { id: "loyaltyCardNumber", label: "loyaltyCardNumber" },
            ]}
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: MEMBER_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={MEMBER_LIST_ID}
        data-test-id="member-list"
        onRowClick={(id: string) => {
          setModal({ member: id as string, mode: Modals.MemberDetailsDrawer });
          setOpen(true);
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "createdAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        customActionsOrder
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ member: id as string, mode: Modals.Activate });
                setOpen(true);
              },
              hideCondition: (item: Row) => !item.metadata?.disabled,
              label: message({ id: "activateAccount" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.Add,
                });
                setOpen(true);
              },
              label: message({ id: "addUnitsTransfer" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.Deduct,
                });
                setOpen(true);
              },
              label: message({ id: "deductUnitsTransfer" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.Anonymize,
                });
                setOpen(true);
              },
              hideCondition: (item: Row) => item.metadata?.anonymized,
              label: message({ id: "anonymizeProfile" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.AssignToTier,
                });
                setOpen(true);
              },
              label: message({ id: "assignToTier" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.ChangePassword,
                });
                setOpen(true);
              },
              label: message({ id: "changePassword" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.Deactivate,
                });
                setOpen(true);
              },
              hideCondition: (item: Row) => item.metadata?.disabled,
              label: message({ id: "deactivateAccount" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.editMember(id as string)),
              label: message({ id: "editProfile" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  member: id as string,
                  mode: Modals.RemoveManuallyAssignedTier,
                });
                setOpen(true);
              },
              hideCondition: (item: Row) =>
                !item.metadata?.manuallyAssignedLevel,
              label: message({ id: "removeManuallyAssignedTier" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({ member: id as string, mode: Modals.Remove });
                setOpen(true);
              },
              label: message({ id: "removeProfile" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.singleMemberGeneralInfo(id as string)),
              label: message({ id: "seeFullProfile" }),
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          onChangePage: (page) => {
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE });
          },
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        clearFilters={() => setFilters([])}
      />

      <MemberModals
        selectedModal={selectedModal}
        open={open}
        close={() => setOpen(false)}
        setModal={setModal}
      />
      <ConfirmationModal
        action={() => handleExportList()}
        close={() => {
          setConfirmationModalOpen(false);
          setErrors(null);
        }}
        errorInfo={errors?.[""]}
        header="exportConfirmation"
        info=""
        open={isConfirmationModalOpen}
        cancelText={message({ id: "cancel" })}
        submitText="export"
        data-test-id="export-modal"
        modalWidth={454}
      />
    </>
  );
}
